import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors, DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ModuleProps } from '../types';
import { plannedElapsedFraction } from './plannedFraction';
import { ResolvedCalendar } from './calendarEngine';
import { useResolvedCalendar } from '../hooks/useResolvedCalendar';
import AppHeader from '../AppHeader';
import { useLanguage } from '../LanguageContext';
import { el, en } from '../i18n/modules';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3000';

// ─── Types ────────────────────────────────────────────────────────────────────

interface ReportBlock { id: string; type: string; config?: { metrics?: string[] }; }

interface Report {
  id: string;
  project_id: string;
  name: string;
  blocks: ReportBlock[];
  created_at: string;
}

const BLOCK_SOURCES: Record<string, string> = {
  schedule_phases: 'Schedule',
  schedule_milestones: 'Schedule',
  budget_evm: 'Budget',
  budget_client_billing: 'Budget',
  budget_vendors: 'Budget',
  risk_top: 'Risk Management',
  risk_summary: 'Risk Management',
  issues_open: 'Issues',
  issues_critical: 'Issues',
};

const PALETTE_GROUPS = [
  { source: 'Schedule',        types: ['schedule_phases', 'schedule_milestones'] },
  { source: 'Budget',          types: ['budget_evm', 'budget_client_billing', 'budget_vendors'] },
  { source: 'Risk Management', types: ['risk_top', 'risk_summary'] },
  { source: 'Issues',          types: ['issues_open', 'issues_critical'] },
];

const EVM_METRIC_KEYS = ['CPI', 'SPI', 'EAC', 'Completion', 'AC', 'EV', 'BAC', 'ETC', 'PV', 'CV', 'CV%', 'SV', 'VAC'] as const;
const BLOCK_METRIC_DEFAULTS: Record<string, string[]> = {
  budget_evm: ['CPI', 'EAC', 'Completion', 'AC'],
};

// ─── Block Renderer ───────────────────────────────────────────────────────────

const fmt = (n: number) => new Intl.NumberFormat('el-GR', { maximumFractionDigits: 0 }).format(n);

function renderBlock(type: string, data: any, lang: string, config?: { metrics?: string[] }, resolvedCalendar?: ResolvedCalendar) {
  if (!data) return <EmptyData lang={lang} />;

  const textColor = '#374151';
  const subColor = '#6b7280';
  const borderColor = '#f3f4f6';

  const table = (headers: string[], rows: (string | React.ReactNode)[][]) => (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ borderBottom: `2px solid ${borderColor}` }}>
            {headers.map((h, i) => (
              <th key={i} style={{ padding: '8px 12px', textAlign: 'left', color: subColor, fontWeight: 600, fontSize: 12 }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr><td colSpan={headers.length} style={{ padding: '20px 12px', textAlign: 'center', color: subColor, fontSize: 12 }}>—</td></tr>
          ) : rows.map((row, i) => (
            <tr key={i} style={{ borderBottom: `1px solid ${borderColor}` }}>
              {row.map((cell, j) => (
                <td key={j} style={{ padding: '10px 12px', color: textColor }}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const badge = (text: string, color: string, bg: string) => (
    <span style={{ padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 600, color, backgroundColor: bg }}>{text}</span>
  );

  const statusBadge = (status: string) => {
    const map: Record<string, [string, string]> = {
      'not_started': ['#6b7280', '#f3f4f6'],
      'in_progress': ['#1d4ed8', '#dbeafe'],
      'completed': ['#166534', '#dcfce7'],
      'Open': ['#991b1b', '#fee2e2'],
      'In Progress': ['#1d4ed8', '#dbeafe'],
      'Resolved': ['#166534', '#dcfce7'],
      'Closed': ['#6b7280', '#f3f4f6'],
      'pending': ['#92400e', '#fef3c7'],
      'received': ['#166534', '#dcfce7'],
      'overdue': ['#991b1b', '#fee2e2'],
    };
    const [c, bg] = map[status] || ['#6b7280', '#f3f4f6'];
    return badge(status, c, bg);
  };

  const metricCards = (items: { label: string; value: string; sub?: string; color?: string }[]) => (
    <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(items.length, 4)}, 1fr)`, gap: 12 }}>
      {items.map((item, i) => (
        <div key={i} style={{ padding: '16px', backgroundColor: '#f9fafb', borderRadius: 10, border: `1px solid ${borderColor}` }}>
          <div style={{ fontSize: 11, color: subColor, fontWeight: 600, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{item.label}</div>
          <div style={{ fontSize: 22, fontWeight: 700, color: item.color || textColor }}>{item.value}</div>
          {item.sub && <div style={{ fontSize: 11, color: subColor, marginTop: 4 }}>{item.sub}</div>}
        </div>
      ))}
    </div>
  );

  // ── Schedule Phases ──
  if (type === 'schedule_phases') {
    const phases: any[] = data.phases || [];
    const activities: any[] = [...(data.activities || []), ...(data.milestones || [])];
    const rows = phases.map(ph => {
      const phActs = activities.filter(a => a.phase_id === ph.id);
      const avg = phActs.length > 0 ? Math.round(phActs.reduce((s: number, a: any) => s + (a.progress_pct || 0), 0) / phActs.length) : 0;
      return [
        ph.title,
        phActs.length,
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ flex: 1, height: 6, backgroundColor: '#e5e7eb', borderRadius: 3, overflow: 'hidden' }}>
            <div style={{ width: `${avg}%`, height: '100%', backgroundColor: avg === 100 ? '#22c55e' : '#4f46e5', borderRadius: 3 }} />
          </div>
          <span style={{ fontSize: 12, color: subColor, width: 32 }}>{avg}%</span>
        </div>,
      ];
    });
    return table(lang === 'en' ? ['Phase', 'Activities', 'Progress'] : ['Φάση', 'Δραστηριότητες', 'Πρόοδος'], rows);
  }

  // ── Schedule Milestones ──
  if (type === 'schedule_milestones') {
    const milestones: any[] = data.milestones || [];
    const startDate = data.start_date ? new Date(data.start_date) : null;
    const rows = milestones.map(m => {
      let dateStr = '—';
      if (startDate && m.end_day) {
        const d = new Date(startDate);
        d.setDate(d.getDate() + (m.end_day - 1));
        dateStr = d.toLocaleDateString(lang === 'en' ? 'en-GB' : 'el-GR', { day: '2-digit', month: '2-digit', year: 'numeric' });
      }
      return [m.title, dateStr, statusBadge(m.status || 'not_started')];
    });
    return table(lang === 'en' ? ['Milestone', 'Date', 'Status'] : ['Ορόσημο', 'Ημερομηνία', 'Κατάσταση'], rows);
  }

  // ── Budget EVM ──
  if (type === 'budget_evm') {
    const teams: any[] = data.teams || [];
    const vendors: any[] = data.vendors || [];
    const totalBudget: number = data.total_budget || 0;
    const schedule = data._schedule;
    const activities: any[] = schedule?.activities || [];
    const nonMilestones = activities.filter((a: any) => !a.is_milestone);
    const actExtras: Record<string, { team_efforts?: Record<string, number> }> = data._scope?.activity_extras || {};

    // costPerMDMap: team name → cost per MD (same as Budget DashboardTab)
    const costPerMDMap: Record<string, number> = {};
    teams.forEach((t: any) => { if (t.budget_md > 0) costPerMDMap[t.name] = t.budget_cost / t.budget_md; });

    // Per-activity planned costs from Scope team_efforts (exact same formula as Budget)
    const activityPlannedCosts: Record<string, number> = {};
    nonMilestones.forEach((a: any) => {
      const efforts = actExtras[a.id]?.team_efforts || {};
      activityPlannedCosts[a.id] = Math.round(Object.entries(efforts).reduce((s: number, [team, md]: [string, any]) => s + (md || 0) * (costPerMDMap[team] ?? 0), 0));
    });
    const totalPlannedFromScope = Object.values(activityPlannedCosts).reduce((s: number, v: any) => s + v, 0);

    const totalHRBudget = teams.reduce((s: number, t: any) => s + (t.budget_cost || 0), 0);
    const totalVendorBudget = vendors.reduce((s: number, v: any) => s + (v.total_contract || 0), 0);
    const BAC = totalPlannedFromScope || totalBudget || totalHRBudget + totalVendorBudget;
    if (!BAC) return <EmptyData lang={lang} />;

    // EV = Σ (progress_pct/100 × planned_cost) per activity (exact same as Budget)
    const EV = nonMilestones.reduce((s: number, a: any) => s + ((a.progress_pct ?? 0) / 100) * (activityPlannedCosts[a.id] || 0), 0);

    // AC from time_entries matched to Budget teams (exact same as Budget getTeamStats)
    let hrAC = 0;
    teams.forEach((t: any) => {
      if (!t.budget_md) return;
      const nameLow = (t.name || '').toLowerCase();
      let teamMD = 0;
      activities.forEach((a: any) => {
        (a.time_entries || []).forEach((te: any) => {
          if (te.team && te.team.toLowerCase() === nameLow) teamMD += te.md || 0;
        });
      });
      hrAC += t.budget_cost * (teamMD / t.budget_md);
    });
    const vendorAC = vendors.reduce((s: number, v: any) =>
      s + (v.invoices || []).filter((i: any) => i.status === 'received').reduce((ss: number, i: any) => ss + (i.actual_amount ?? i.amount ?? 0), 0), 0);
    const AC = hrAC + vendorAC;

    const pct = BAC > 0 ? Math.round((EV / BAC) * 100) : 0;
    const CPI = AC > 0 ? EV / AC : null;
    const EAC = CPI && CPI > 0 ? BAC / CPI : BAC;
    const ETC = EAC - AC;
    const CV = AC > 0 ? EV - AC : null;
    const CVpct = CV !== null && EV > 0 ? (CV / EV) * 100 : null;
    const VAC = BAC - EAC;

    // PV: time-elapsed per activity (same formula as Budget DashboardTab)
    const today = new Date();
    const projStart = schedule?.start_date ? new Date(schedule.start_date) : null;
    const PV = totalPlannedFromScope > 0 && projStart
      ? nonMilestones.reduce((s: number, a: any) => {
          const planned = activityPlannedCosts[a.id] || 0;
          if (!planned) return s;
          return s + planned * plannedElapsedFraction(projStart, a.start_day || 1, a.end_day || a.start_day || 1, today, resolvedCalendar);
        }, 0)
      : null;
    const SPI = PV && PV > 0 ? EV / PV : null;
    const SV = PV !== null ? EV - PV : null;

    const cpiColor = (v: number | null) => !v ? '#6b7280' : v >= 1 ? '#166534' : '#991b1b';
    const varColor = (v: number | null) => v === null ? '#6b7280' : v >= 0 ? '#166534' : '#991b1b';

    const selectedMetrics = config?.metrics || BLOCK_METRIC_DEFAULTS.budget_evm;
    const allMetrics: Record<string, { label: string; value: string; sub?: string; color?: string }> = {
      CPI:        { label: 'CPI', value: CPI !== null ? CPI.toFixed(2) : '—', sub: CPI !== null ? (CPI >= 1 ? (lang === 'en' ? 'On budget' : 'Εντός budget') : (lang === 'en' ? 'Over budget' : 'Υπέρβαση')) : '—', color: cpiColor(CPI) },
      SPI:        { label: 'SPI', value: SPI !== null ? SPI.toFixed(2) : '—', sub: SPI !== null ? (SPI >= 1 ? (lang === 'en' ? 'On schedule' : 'Εντός χρόνου') : (lang === 'en' ? 'Delayed' : 'Καθυστέρηση')) : '—', color: cpiColor(SPI) },
      EAC:        { label: 'EAC', value: `€${fmt(Math.round(EAC))}`, sub: `BAC: €${fmt(BAC)}`, color: EAC > BAC ? '#991b1b' : '#166534' },
      Completion: { label: lang === 'en' ? 'Completion' : 'Ολοκλήρωση', value: `${pct}%`, sub: `EV: €${fmt(Math.round(EV))}` },
      AC:         { label: 'AC', value: `€${fmt(Math.round(AC))}`, sub: CV !== null ? `CV: ${CV >= 0 ? '+' : ''}€${fmt(Math.round(CV))}` : '—', color: cpiColor(CPI) },
      EV:         { label: 'EV', value: `€${fmt(Math.round(EV))}`, sub: `${pct}% × BAC` },
      BAC:        { label: 'BAC', value: `€${fmt(BAC)}` },
      ETC:        { label: 'ETC', value: `€${fmt(Math.round(ETC))}`, sub: lang === 'en' ? 'Remaining cost' : 'Εναπομείναν κόστος', color: ETC < 0 ? '#991b1b' : '#374151' },
      PV:         { label: 'PV', value: PV !== null ? `€${fmt(Math.round(PV))}` : '—', sub: lang === 'en' ? 'Planned Value today' : 'Planned Value σήμερα' },
      CV:         { label: 'CV', value: CV !== null ? `${CV >= 0 ? '+' : ''}€${fmt(Math.round(CV))}` : '—', sub: CV !== null ? (CV >= 0 ? (lang === 'en' ? 'Under budget' : 'Εντός budget') : (lang === 'en' ? 'Over budget' : 'Υπέρβαση')) : '—', color: varColor(CV) },
      'CV%':      { label: 'CV%', value: CVpct !== null ? `${CVpct >= 0 ? '+' : ''}${CVpct.toFixed(1)}%` : '—', sub: CVpct !== null ? (CVpct >= 0 ? (lang === 'en' ? 'Under budget' : 'Κάτω προϋπολογισμού') : (lang === 'en' ? 'Over budget' : 'Υπέρβαση')) : '—', color: varColor(CVpct) },
      SV:         { label: 'SV', value: SV !== null ? `${SV >= 0 ? '+' : ''}€${fmt(Math.round(SV))}` : '—', sub: SV !== null ? (SV >= 0 ? (lang === 'en' ? 'Ahead' : 'Εντός χρόνου') : (lang === 'en' ? 'Behind schedule' : 'Χρονική καθυστέρηση')) : '—', color: varColor(SV) },
      VAC:        { label: 'VAC', value: `${VAC >= 0 ? '+' : ''}€${fmt(Math.round(VAC))}`, sub: VAC >= 0 ? (lang === 'en' ? 'Cost margin' : 'Περιθώριο κόστους') : (lang === 'en' ? 'Expected overrun' : 'Αναμενόμενη υπέρβαση'), color: varColor(VAC) },
    };

    return metricCards(selectedMetrics.filter(k => allMetrics[k]).map(k => allMetrics[k]));
  }

  // ── Budget Client Billing ──
  if (type === 'budget_client_billing') {
    const billing: any[] = data.client_billing || [];
    const rows = billing.map(b => [
      b.description || '—',
      b.period || '—',
      b.expected_revenue ? `€${fmt(b.expected_revenue)}` : '—',
      b.actual_revenue ? `€${fmt(b.actual_revenue)}` : '—',
      statusBadge(b.status || 'pending'),
    ]);
    return table(
      lang === 'en' ? ['Description', 'Period', 'Expected', 'Actual', 'Status'] : ['Περιγραφή', 'Περίοδος', 'Αναμενόμενο', 'Πραγματικό', 'Κατάσταση'],
      rows,
    );
  }

  // ── Budget Vendors ──
  if (type === 'budget_vendors') {
    const vendors: any[] = data.vendors || [];
    const rows = vendors.map(v => {
      const invoiced = (v.invoices || []).reduce((s: number, i: any) => s + (i.amount || 0), 0);
      const received = (v.invoices || []).filter((i: any) => i.status === 'received').reduce((s: number, i: any) => s + (i.actual_amount || i.amount || 0), 0);
      return [v.name, v.role || '—', `€${fmt(v.total_contract || 0)}`, `€${fmt(invoiced)}`, `€${fmt(received)}`];
    });
    return table(
      lang === 'en' ? ['Vendor', 'Role', 'Contract', 'Invoiced', 'Received'] : ['Προμηθευτής', 'Ρόλος', 'Σύμβαση', 'Τιμολογηθέν', 'Εισπραχθέν'],
      rows,
    );
  }

  // ── Risk Top ──
  if (type === 'risk_top') {
    const risks: any[] = (data.risks || []).sort((a: any, b: any) => b.score - a.score).slice(0, 8);
    const scoreColor = (s: number) => s >= 3.5 ? '#991b1b' : s >= 2 ? '#92400e' : '#166534';
    const rows = risks.map(r => [
      r.title,
      r.risk_category || '—',
      <span style={{ fontWeight: 600, color: scoreColor(r.score) }}>{r.score?.toFixed(1)}</span>,
      statusBadge(r.status || 'Open'),
    ]);
    return table(
      lang === 'en' ? ['Risk', 'Category', 'Score', 'Status'] : ['Ρίσκο', 'Κατηγορία', 'Score', 'Κατάσταση'],
      rows,
    );
  }

  // ── Risk Summary ──
  if (type === 'risk_summary') {
    const risks: any[] = data.risks || [];
    return metricCards([
      { label: lang === 'en' ? 'Total Risks' : 'Σύνολο', value: String(risks.length) },
      { label: 'Open', value: String(risks.filter(r => r.status === 'Open').length), color: '#991b1b' },
      { label: 'In Progress', value: String(risks.filter(r => r.status === 'In Progress').length), color: '#1d4ed8' },
      { label: 'Closed', value: String(risks.filter(r => r.status === 'Closed').length), color: '#166534' },
    ]);
  }

  // ── Issues Open ──
  if (type === 'issues_open') {
    const issues: any[] = (data.issues || []).filter((i: any) => i.status === 'Open' || i.status === 'In Progress');
    const priorityColor: Record<string, [string, string]> = {
      Critical: ['#991b1b', '#fee2e2'],
      High: ['#92400e', '#fef3c7'],
      Medium: ['#1d4ed8', '#dbeafe'],
      Low: ['#6b7280', '#f3f4f6'],
    };
    const rows = issues.map(i => [
      i.title,
      <span style={{ padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 600, color: priorityColor[i.priority]?.[0] || '#6b7280', backgroundColor: priorityColor[i.priority]?.[1] || '#f3f4f6' }}>{i.priority}</span>,
      statusBadge(i.status),
      i.owner || '—',
    ]);
    return table(
      lang === 'en' ? ['Issue', 'Priority', 'Status', 'Owner'] : ['Issue', 'Προτεραιότητα', 'Κατάσταση', 'Υπεύθυνος'],
      rows,
    );
  }

  // ── Issues Critical ──
  if (type === 'issues_critical') {
    const issues: any[] = (data.issues || []).filter((i: any) => (i.priority === 'Critical' || i.priority === 'High') && i.status !== 'Closed' && i.status !== 'Resolved');
    const rows = issues.map(i => [
      i.title,
      i.category || '—',
      i.deadline || '—',
      statusBadge(i.status),
    ]);
    return table(
      lang === 'en' ? ['Issue', 'Category', 'Deadline', 'Status'] : ['Issue', 'Κατηγορία', 'Προθεσμία', 'Κατάσταση'],
      rows,
    );
  }

  return <EmptyData lang={lang} />;
}

function EmptyData({ lang }: { lang: string }) {
  return <p style={{ color: '#9ca3af', fontSize: 13, textAlign: 'center', padding: '20px 0', margin: 0 }}>{lang === 'en' ? 'No data available' : 'Δεν υπάρχουν δεδομένα'}</p>;
}

// ─── Sortable Block ───────────────────────────────────────────────────────────

function SortableBlock({ block, data, label, canEdit, onRemove, onConfigure, lang, resolvedCalendar }: {
  block: ReportBlock; data: any; label: string; canEdit: boolean;
  onRemove: (id: string) => void; onConfigure: (id: string) => void; lang: string; resolvedCalendar?: ResolvedCalendar;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: block.id });
  const [minimized, setMinimized] = useState(false);
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.4 : 1 };
  const isConfigurable = !!BLOCK_METRIC_DEFAULTS[block.type];

  return (
    <div ref={setNodeRef} style={{ ...style, backgroundColor: 'white', borderRadius: 12, border: '1px solid #e5e7eb', marginBottom: 16, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 16px', borderBottom: minimized ? 'none' : '1px solid #f3f4f6', backgroundColor: '#fafafa' }}>
        {canEdit && (
          <span {...attributes} {...listeners} style={{ cursor: 'grab', color: '#d1d5db', fontSize: 16, userSelect: 'none', flexShrink: 0 }} title={lang === 'en' ? 'Drag to reorder' : 'Σύρε για αναδιάταξη'}>⠿</span>
        )}
        <span style={{ flex: 1, fontSize: 13, fontWeight: 600, color: '#374151' }}>{label}</span>
        <span style={{ fontSize: 11, color: '#9ca3af', backgroundColor: '#f3f4f6', padding: '2px 8px', borderRadius: 999 }}>{BLOCK_SOURCES[block.type]}</span>
        {canEdit && isConfigurable && (
          <button onClick={() => onConfigure(block.id)} style={{ background: 'none', border: '1px solid #e5e7eb', borderRadius: 6, padding: '2px 8px', cursor: 'pointer', fontSize: 11, color: '#6b7280' }} title={lang === 'en' ? 'Configure metrics' : 'Επιλογή δεικτών'}>
            ⚙
          </button>
        )}
        <button
          onClick={() => setMinimized(v => !v)}
          style={{ background: 'none', border: 'none', padding: '2px 4px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
          title={minimized ? (lang === 'en' ? 'Expand' : 'Ανάπτυξη') : (lang === 'en' ? 'Collapse' : 'Σύμπτυξη')}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ transform: minimized ? 'rotate(-90deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </button>
        {canEdit && (
          <button onClick={() => onRemove(block.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#d1d5db', fontSize: 16, padding: '0 4px', lineHeight: 1 }} title={lang === 'en' ? 'Remove' : 'Αφαίρεση'}>×</button>
        )}
      </div>
      {!minimized && (
        <div style={{ padding: '16px' }}>
          {renderBlock(block.type, data, lang, block.config, resolvedCalendar)}
        </div>
      )}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

const Reporting: React.FC<ModuleProps> = ({ project, user, token, onBack, canEdit, onLogout, onProfileEdit }) => {
  const { language } = useLanguage();
  const M = language === 'en' ? en : el;
  const L = M.reporting;
  const C = M.common;
  const headers = { Authorization: `Bearer ${token}` };

  const [reports, setReports] = useState<Report[]>([]);
  const [selected, setSelected] = useState<Report | null>(null);
  const [moduleData, setModuleData] = useState<Record<string, any>>({});
  const [loadingData, setLoadingData] = useState(false);
  const [showPalette, setShowPalette] = useState(false);
  const [showNewModal, setShowNewModal] = useState(false);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [nameInput, setNameInput] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [configBlockId, setConfigBlockId] = useState<string | null>(null);
  const [configMetrics, setConfigMetrics] = useState<string[]>([]);
  const [pendingBlockType, setPendingBlockType] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // Working Calendar: same resolved calendar used for the budget_evm block's PV, derived
  // from the Budget module's embedded _schedule (attached server-side by reporting.service.ts).
  const scheduleForCalendar = moduleData['Budget']?._schedule;
  const calendarRangeTo = (() => {
    const acts = scheduleForCalendar?.activities || [];
    if (!scheduleForCalendar?.start_date || acts.length === 0) return null;
    const maxEndDay = Math.max(...acts.map((a: any) => a.end_day || a.start_day || 1), 1);
    const d = new Date(scheduleForCalendar.start_date);
    d.setDate(d.getDate() + maxEndDay + 30);
    return d.toISOString().slice(0, 10);
  })();
  const resolvedCalendar = useResolvedCalendar(project.calendar_id, scheduleForCalendar?.start_date, calendarRangeTo, token);

  useEffect(() => {
    axios.get(`${API}/reporting/${project.id}`, { headers })
      .then(r => setReports(r.data || []))
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchModuleData = useCallback(async (blocks: ReportBlock[]) => {
    const sourcesSet: Record<string, boolean> = {};
    blocks.forEach(b => { if (BLOCK_SOURCES[b.type]) sourcesSet[BLOCK_SOURCES[b.type]] = true; });
    const sources = Object.keys(sourcesSet);
    if (!sources.length) return;
    setLoadingData(true);
    const results: Record<string, any> = { ...moduleData };
    await Promise.all(sources.map(async source => {
      if (results[source]) return;
      try {
        const r = await axios.get(`${API}/reporting/${project.id}/data/${encodeURIComponent(source)}`, { headers });
        results[source] = r.data;
      } catch { results[source] = null; }
    }));
    setModuleData(results);
    setLoadingData(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moduleData]);

  const selectReport = (report: Report) => {
    setSelected(report);
    setShowPalette(false);
    setEditingName(false);
    fetchModuleData(report.blocks);
  };

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      const r = await axios.post(`${API}/reporting/${project.id}`, { name: newName.trim() }, { headers });
      const created = r.data;
      setReports(prev => [...prev, created]);
      setSelected(created);
      setShowNewModal(false);
      setNewName('');
    } catch {} finally { setCreating(false); }
  };

  const saveBlocks = async (blocks: ReportBlock[]) => {
    if (!selected) return;
    setSaving(true);
    try {
      const r = await axios.put(`${API}/reporting/${project.id}/${selected.id}`, { blocks }, { headers });
      const updated = r.data;
      setSelected(updated);
      setReports(prev => prev.map(rp => rp.id === updated.id ? updated : rp));
      await fetchModuleData(blocks);
    } catch {} finally { setSaving(false); }
  };

  const handleAddBlock = (type: string) => {
    if (!selected) return;
    setShowPalette(false);
    if (BLOCK_METRIC_DEFAULTS[type]) {
      setPendingBlockType(type);
      setConfigMetrics([...BLOCK_METRIC_DEFAULTS[type]]);
      return;
    }
    const newBlock: ReportBlock = { id: `${type}_${Date.now()}`, type };
    saveBlocks([...selected.blocks, newBlock]);
  };

  const handleConfirmPendingBlock = () => {
    if (!selected || !pendingBlockType) return;
    const newBlock: ReportBlock = { id: `${pendingBlockType}_${Date.now()}`, type: pendingBlockType, config: { metrics: configMetrics } };
    saveBlocks([...selected.blocks, newBlock]);
    setPendingBlockType(null);
  };

  const handleOpenConfig = (blockId: string) => {
    const block = selected?.blocks.find(b => b.id === blockId);
    if (!block) return;
    setConfigBlockId(blockId);
    setConfigMetrics(block.config?.metrics || BLOCK_METRIC_DEFAULTS[block.type] || []);
  };

  const handleSaveConfig = () => {
    if (!selected || !configBlockId) return;
    const blocks = selected.blocks.map(b => b.id === configBlockId ? { ...b, config: { ...b.config, metrics: configMetrics } } : b);
    saveBlocks(blocks);
    setConfigBlockId(null);
  };

  const handleRemoveBlock = (blockId: string) => {
    if (!selected) return;
    saveBlocks(selected.blocks.filter(b => b.id !== blockId));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id || !selected) return;
    const oldIndex = selected.blocks.findIndex(b => b.id === active.id);
    const newIndex = selected.blocks.findIndex(b => b.id === over.id);
    const reordered = arrayMove(selected.blocks, oldIndex, newIndex);
    setSelected({ ...selected, blocks: reordered });
    saveBlocks(reordered);
  };

  const handleRename = async () => {
    if (!selected || !nameInput.trim()) { setEditingName(false); return; }
    try {
      const r = await axios.put(`${API}/reporting/${project.id}/${selected.id}`, { name: nameInput.trim() }, { headers });
      const updated = r.data;
      setSelected(updated);
      setReports(prev => prev.map(rp => rp.id === updated.id ? updated : rp));
    } catch {} finally { setEditingName(false); }
  };

  const handleDelete = async () => {
    if (!selected) return;
    try {
      await axios.delete(`${API}/reporting/${project.id}/${selected.id}`, { headers });
      setReports(prev => prev.filter(r => r.id !== selected.id));
      setSelected(null);
      setShowDeleteModal(false);
    } catch {}
  };

  const addedTypes = selected?.blocks.map(b => b.type) || [];

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f0f2f5', display: 'flex', flexDirection: 'column' }}>
      <AppHeader user={user} onHome={onBack} onProfileEdit={onProfileEdit} onLogout={onLogout} />

      {/* Breadcrumb */}
      <div style={{ padding: '12px 32px', backgroundColor: 'white', borderBottom: '1px solid #e5e7eb', fontSize: 14, position: 'sticky', top: 64, zIndex: 99 }}>
        <span style={{ color: '#4f46e5', cursor: 'pointer', fontWeight: 500 }} onClick={onBack}>{C.myProjects}</span>
        <span style={{ color: '#9ca3af', margin: '0 8px' }}>›</span>
        <span style={{ color: '#374151', fontWeight: 500 }}>{L.title}</span>
      </div>

      {/* Main layout */}
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden', height: 'calc(100vh - 113px)' }}>

        {/* Sidebar */}
        <div style={{ width: 260, backgroundColor: 'white', borderRight: '1px solid #e5e7eb', display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
          <div style={{ padding: '16px 16px 12px', borderBottom: '1px solid #f3f4f6' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 }}>{L.title}</div>
            {canEdit && (
              <button
                onClick={() => { setShowNewModal(true); setNewName(''); }}
                style={{ width: '100%', padding: '8px 12px', backgroundColor: '#4f46e5', color: 'white', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', textAlign: 'left' }}
              >
                {L.newReport}
              </button>
            )}
          </div>

          <div style={{ flex: 1, overflowY: 'auto', padding: '8px' }}>
            {reports.length === 0 ? (
              <p style={{ color: '#9ca3af', fontSize: 12, textAlign: 'center', padding: '20px 8px', margin: 0 }}>{L.noReports}</p>
            ) : reports.map(r => (
              <div
                key={r.id}
                onClick={() => selectReport(r)}
                style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 10px', borderRadius: 7, cursor: 'pointer', marginBottom: 2, backgroundColor: selected?.id === r.id ? '#eef2ff' : 'transparent' }}
              >
                <span style={{ fontSize: 14, flexShrink: 0 }}>📋</span>
                <span style={{ flex: 1, fontSize: 13, fontWeight: selected?.id === r.id ? 600 : 400, color: selected?.id === r.id ? '#4f46e5' : '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.name}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Report area */}
        <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
          {!selected ? (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#9ca3af' }}>
              <span style={{ fontSize: 48, marginBottom: 16 }}>📊</span>
              <p style={{ fontSize: 15, fontWeight: 500, margin: '0 0 4px' }}>{reports.length === 0 ? L.noReports : L.selectReport}</p>
              <p style={{ fontSize: 13, margin: 0 }}>{reports.length === 0 ? L.noReportsHint : ''}</p>
            </div>
          ) : (
            <>
              {/* Report header */}
              <div style={{ padding: '16px 24px', backgroundColor: 'white', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', gap: 12 }}>
                {editingName ? (
                  <input
                    autoFocus
                    value={nameInput}
                    onChange={e => setNameInput(e.target.value)}
                    onBlur={handleRename}
                    onKeyDown={e => { if (e.key === 'Enter') handleRename(); if (e.key === 'Escape') setEditingName(false); }}
                    style={{ flex: 1, fontSize: 18, fontWeight: 700, color: '#1a1a2e', border: 'none', borderBottom: '2px solid #4f46e5', outline: 'none', background: 'transparent', padding: '2px 0' }}
                  />
                ) : (
                  <h1
                    style={{ flex: 1, fontSize: 18, fontWeight: 700, color: '#1a1a2e', margin: 0, cursor: canEdit ? 'pointer' : 'default' }}
                    onClick={() => { if (canEdit) { setNameInput(selected.name); setEditingName(true); } }}
                    title={canEdit ? L.renameReport : undefined}
                  >
                    {selected.name}
                  </h1>
                )}
                {saving && <span style={{ fontSize: 12, color: '#9ca3af' }}>...</span>}
                {canEdit && (
                  <>
                    <button
                      onClick={() => setShowPalette(v => !v)}
                      style={{ padding: '7px 14px', backgroundColor: '#4f46e5', color: 'white', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
                    >
                      {L.addBlock}
                    </button>
                    <button
                      onClick={() => setShowDeleteModal(true)}
                      style={{ padding: '7px 12px', backgroundColor: 'white', color: '#ef4444', border: '1px solid #fca5a5', borderRadius: 8, fontSize: 13, cursor: 'pointer' }}
                    >
                      🗑
                    </button>
                  </>
                )}
              </div>

              {/* Block palette */}
              {showPalette && canEdit && (
                <div style={{ backgroundColor: 'white', borderBottom: '1px solid #e5e7eb', padding: '16px 24px' }}>
                  <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
                    {PALETTE_GROUPS.map(group => (
                      <div key={group.source}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 8 }}>
                          {L.sources[group.source] || group.source}
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                          {group.types.map(type => {
                            const alreadyAdded = addedTypes.includes(type);
                            return (
                              <button
                                key={type}
                                onClick={() => !alreadyAdded && handleAddBlock(type)}
                                disabled={alreadyAdded}
                                style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid', fontSize: 12, fontWeight: 500, cursor: alreadyAdded ? 'not-allowed' : 'pointer', textAlign: 'left', borderColor: alreadyAdded ? '#e5e7eb' : '#c7d2fe', backgroundColor: alreadyAdded ? '#f9fafb' : '#eef2ff', color: alreadyAdded ? '#9ca3af' : '#4f46e5' }}
                              >
                                {L.blocks[type] || type} {alreadyAdded ? '✓' : ''}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Blocks */}
              <div style={{ flex: 1, padding: '20px 24px' }}>
                {loadingData ? (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 120, color: '#9ca3af', fontSize: 14 }}>...</div>
                ) : selected.blocks.length === 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 200, color: '#9ca3af' }}>
                    <span style={{ fontSize: 36, marginBottom: 12 }}>📄</span>
                    <p style={{ fontSize: 14, fontWeight: 500, margin: '0 0 4px' }}>{L.noBlocks}</p>
                    <p style={{ fontSize: 13, margin: 0 }}>{canEdit ? L.noBlocksHint : ''}</p>
                  </div>
                ) : (
                  <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                    <SortableContext items={selected.blocks.map(b => b.id)} strategy={verticalListSortingStrategy}>
                      {selected.blocks.map(block => (
                        <SortableBlock
                          key={block.id}
                          block={block}
                          data={moduleData[BLOCK_SOURCES[block.type]]}
                          label={L.blocks[block.type] || block.type}
                          canEdit={canEdit}
                          onRemove={handleRemoveBlock}
                          onConfigure={handleOpenConfig}
                          resolvedCalendar={resolvedCalendar}
                          lang={language}
                        />
                      ))}
                    </SortableContext>
                  </DndContext>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* New Report Modal */}
      {showNewModal && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: 'white', borderRadius: 16, padding: 32, width: '100%', maxWidth: 400, boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }}>
            <h2 style={{ fontSize: 18, fontWeight: 700, color: '#1a1a2e', margin: '0 0 20px' }}>{L.newReport}</h2>
            <input
              autoFocus
              value={newName}
              onChange={e => setNewName(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleCreate(); if (e.key === 'Escape') setShowNewModal(false); }}
              placeholder={L.newReportPlaceholder}
              style={{ width: '100%', padding: '10px 12px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box', marginBottom: 16 }}
            />
            <div style={{ display: 'flex', gap: 12 }}>
              <button onClick={() => setShowNewModal(false)} style={{ flex: 1, padding: 10, borderRadius: 8, border: '1px solid #e5e7eb', backgroundColor: 'white', fontSize: 14, cursor: 'pointer', color: '#374151' }}>{C.cancel}</button>
              <button onClick={handleCreate} disabled={!newName.trim() || creating} style={{ flex: 1, padding: 10, borderRadius: 8, border: 'none', backgroundColor: newName.trim() ? '#4f46e5' : '#e5e7eb', color: newName.trim() ? 'white' : '#9ca3af', fontSize: 14, fontWeight: 600, cursor: newName.trim() ? 'pointer' : 'not-allowed' }}>{creating ? '...' : L.create}</button>
            </div>
          </div>
        </div>
      )}

      {/* Metric Config Modal (add new block OR edit existing) */}
      {(pendingBlockType || configBlockId) && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: 'white', borderRadius: 16, padding: 32, width: '100%', maxWidth: 420, boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }}>
            <h2 style={{ fontSize: 17, fontWeight: 700, color: '#1a1a2e', margin: '0 0 6px' }}>
              {language === 'en' ? 'Select metrics to display' : 'Επιλογή δεικτών προς εμφάνιση'}
            </h2>
            <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 20px' }}>
              {language === 'en' ? 'Choose which EVM indicators to include in the report.' : 'Επίλεξε ποιους δείκτες EVM να συμπεριληφθούν στην αναφορά.'}
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 24 }}>
              {EVM_METRIC_KEYS.map(key => {
                const checked = configMetrics.includes(key);
                return (
                  <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 8, border: `1px solid ${checked ? '#c7d2fe' : '#e5e7eb'}`, backgroundColor: checked ? '#eef2ff' : 'white', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => setConfigMetrics(prev => checked ? prev.filter(m => m !== key) : [...prev, key])}
                      style={{ accentColor: '#4f46e5', width: 15, height: 15 }}
                    />
                    <span style={{ fontSize: 13, fontWeight: 600, color: checked ? '#4f46e5' : '#374151' }}>{key}</span>
                  </label>
                );
              })}
            </div>
            <div style={{ display: 'flex', gap: 12 }}>
              <button
                onClick={() => { setPendingBlockType(null); setConfigBlockId(null); }}
                style={{ flex: 1, padding: 10, borderRadius: 8, border: '1px solid #e5e7eb', backgroundColor: 'white', fontSize: 14, cursor: 'pointer', color: '#374151' }}
              >{C.cancel}</button>
              <button
                onClick={() => pendingBlockType ? handleConfirmPendingBlock() : handleSaveConfig()}
                disabled={configMetrics.length === 0}
                style={{ flex: 1, padding: 10, borderRadius: 8, border: 'none', backgroundColor: configMetrics.length > 0 ? '#4f46e5' : '#e5e7eb', color: configMetrics.length > 0 ? 'white' : '#9ca3af', fontSize: 14, fontWeight: 600, cursor: configMetrics.length > 0 ? 'pointer' : 'not-allowed' }}
              >{pendingBlockType ? (language === 'en' ? 'Add block' : 'Προσθήκη') : C.save}</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Report Modal */}
      {showDeleteModal && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: 'white', borderRadius: 16, padding: 32, width: '100%', maxWidth: 400, boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 24 }}>
              <div style={{ width: 56, height: 56, borderRadius: '50%', backgroundColor: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16, fontSize: 24 }}>🗑</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: '#111827', marginBottom: 6 }}>{L.deleteReportConfirm}</div>
              <div style={{ fontSize: 13, color: '#6b7280', textAlign: 'center' }}><strong>{selected?.name}</strong><br />{L.deleteReportHint}</div>
            </div>
            <div style={{ display: 'flex', gap: 12 }}>
              <button onClick={() => setShowDeleteModal(false)} style={{ flex: 1, padding: 10, borderRadius: 8, border: '1px solid #e5e7eb', backgroundColor: 'white', fontSize: 14, cursor: 'pointer', color: '#374151' }}>{C.cancel}</button>
              <button onClick={handleDelete} style={{ flex: 1, padding: 10, borderRadius: 8, border: 'none', backgroundColor: '#ef4444', color: 'white', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>{L.deleteReport}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Reporting;
